/* Historical Sources admin panel — CRUD for the "Our sources" grid on
 * /historical. Admin can add / edit / hide / delete each source, upload a
 * true cover image, or regenerate a Nano Banana faux-vintage cover. */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  BookOpen, PlusCircle, Trash, PencilSimple, UploadSimple, Sparkle,
  Eye, EyeSlash, X as XIcon, ArrowSquareOut, MagicWand,
} from "@phosphor-icons/react";
import useAiJob from "@/hooks/useAiJob";

const EMPTY_FORM = {
  title: "",
  author: "",
  year: "",
  country: "",
  archive_url: "",
  visible: false,   // default hidden — admin flips it when ready to go live
};

export default function HistoricalSourcesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // full source doc when open
  const [showAdd, setShowAdd] = useState(false);
  const [newRow, setNewRow] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const uploadRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/historical/sources");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load sources");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const bulkSeedJob = useAiJob({
    statusUrl: (id) => `/admin/historical/sources/bulk-seed/status/${id}`,
    onDone: (job) => {
      const inserted = job.inserted ?? job.total ?? 0;
      toast.success(`Bulk-seed complete — added ${inserted} hidden source${inserted === 1 ? "" : "s"} for review`);
      load();
    },
    onCancelled: (job) => {
      toast(`Cancelled — kept ${job.inserted ?? 0} already seeded`);
      load();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e?.message || "Bulk seed failed"),
  });

  const runBulkSeed = async () => {
    if (!window.confirm("Scan all Historical recipes and auto-add any missing source book (with an AI cover)? New sources will be hidden until you review them.")) return;
    try {
      await bulkSeedJob.start(async () => {
        const { data } = await api.post("/admin/historical/sources/bulk-seed-from-recipes");
        // If the endpoint responded synchronously with `status: "done"`
        // (nothing to seed) our hook short-circuits via the no-job_id branch.
        if (data.status === "done" && !data.job_id) {
          toast.info(data.note || "Everything is already tracked.");
          return { ...data, inserted: data.inserted || 0 };
        }
        return data;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk seed failed");
    }
  };

  const createSource = async () => {
    if (!newRow.title.trim()) return toast.error("Title is required");
    setBusy(true);
    try {
      await api.post("/admin/historical/sources", {
        ...newRow,
        year: newRow.year ? parseInt(newRow.year, 10) : null,
      });
      toast.success(`Added "${newRow.title}" — Nano Banana is drafting a cover`);
      setNewRow(EMPTY_FORM);
      setShowAdd(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const patch = async (id, body) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/historical/sources/${id}`, body);
      setItems((rows) => rows.map((r) => (r.source_id === id ? { ...r, ...data } : r)));
      if (editing?.source_id === id) setEditing({ ...editing, ...data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const toggleVisibility = (row) => patch(row.source_id, { visible: !row.visible });

  const removeSource = async (row) => {
    if (!window.confirm(`Delete "${row.title}"? This can't be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/historical/sources/${row.source_id}`);
      toast.success("Deleted");
      setItems((rows) => rows.filter((r) => r.source_id !== row.source_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const uploadCover = async (row, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/historical/sources/${row.source_id}/upload-cover`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Cover uploaded");
      setItems((rows) => rows.map((r) => (r.source_id === row.source_id ? { ...r, cover_url: data.cover_url } : r)));
      if (editing?.source_id === row.source_id) setEditing({ ...editing, cover_url: data.cover_url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const regenerateCover = async (row) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/historical/sources/${row.source_id}/generate-cover`);
      toast.success("AI cover regenerated");
      setItems((rows) => rows.map((r) => (r.source_id === row.source_id ? { ...r, cover_url: data.cover_url } : r)));
      if (editing?.source_id === row.source_id) setEditing({ ...editing, cover_url: data.cover_url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
    } finally { setBusy(false); }
  };

  const saveEdits = async () => {
    if (!editing) return;
    await patch(editing.source_id, {
      title: editing.title,
      author: editing.author,
      year: editing.year || null,
      country: editing.country,
      archive_url: editing.archive_url || "",
      visible: !!editing.visible,
    });
    toast.success("Saved — live on /historical for visible rows");
    setEditing(null);
  };

  return (
    <div className="space-y-6" data-testid="historical-sources-panel">
      <div className="flex items-center justify-between">
        <div>
          <p className="overline">Historical</p>
          <h2 className="font-display font-black text-2xl text-rf-ink flex items-center gap-2">
            <BookOpen size={28} weight="duotone" /> Source books
          </h2>
          <p className="text-sm text-rf-ink-soft mt-1 max-w-2xl">
            Populates the &quot;Our sources&quot; grid at the bottom of /historical. New sources added by the AI batch generator land here <b>hidden</b> — review the auto-generated cover, upload a real jacket if the AI got it wrong, then flip the visibility toggle.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <button
            onClick={runBulkSeed}
            disabled={bulkSeedJob.busy}
            data-testid="historical-sources-bulk-seed"
            className="btn-outline-ink inline-flex items-center gap-2 !py-2 !px-4 text-sm disabled:opacity-60"
          >
            <MagicWand size={16} weight="bold" /> {bulkSeedJob.busy ? "Seeding…" : "Bulk seed from recipes"}
          </button>
          <button
            onClick={() => setShowAdd((v) => !v)}
            data-testid="historical-sources-add-btn"
            className="btn-amber inline-flex items-center gap-2 !py-2 !px-4 text-sm"
          >
            <PlusCircle size={16} weight="bold" /> Add source
          </button>
        </div>
      </div>

      {bulkSeedJob.progress && bulkSeedJob.progress.total > 0 && (
        <div className="bg-white rounded-2xl border border-rf-ink/10 p-4" data-testid="historical-sources-bulk-progress">
          <div className="flex items-center justify-between text-xs font-bold text-rf-ink-soft mb-2">
            <span>
              {bulkSeedJob.progress.status === "done"
                ? "Done"
                : bulkSeedJob.progress.status === "cancelled"
                ? "Cancelled"
                : `Seeding ${bulkSeedJob.progress.processed} / ${bulkSeedJob.progress.total} — ${bulkSeedJob.progress.inserted} added, ${bulkSeedJob.progress.skipped} skipped`}
            </span>
            <div className="flex items-center gap-3">
              <span>{Math.round(((bulkSeedJob.progress.processed || 0) / (bulkSeedJob.progress.total || 1)) * 100)}%</span>
              {bulkSeedJob.progress.status === "running" && !bulkSeedJob.progress.cancel_requested && (
                <button type="button" onClick={bulkSeedJob.cancel} className="text-rf-ink-soft hover:text-red-600 underline font-semibold" data-testid="historical-sources-bulk-cancel">
                  Cancel
                </button>
              )}
              {bulkSeedJob.progress.cancel_requested && bulkSeedJob.progress.status === "running" && (
                <span className="text-amber-600 font-semibold">Cancelling…</span>
              )}
            </div>
          </div>
          <div className="w-full h-2 rounded-full bg-rf-ink/10 overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${bulkSeedJob.progress.status === "cancelled" ? "bg-rf-ink-soft" : "bg-rf-orange"}`}
              style={{ width: `${Math.min(100, Math.round(((bulkSeedJob.progress.processed || 0) / (bulkSeedJob.progress.total || 1)) * 100))}%` }}
            />
          </div>
        </div>
      )}

      {showAdd && (
        <div className="bg-white rounded-2xl border border-rf-amber/30 p-5 space-y-3" data-testid="historical-sources-add-card">
          <p className="overline">New source book</p>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Title *</label>
              <input className="input-rf w-full text-sm" value={newRow.title} onChange={(e) => setNewRow({ ...newRow, title: e.target.value })} data-testid="new-source-title" />
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Author</label>
              <input className="input-rf w-full text-sm" value={newRow.author} onChange={(e) => setNewRow({ ...newRow, author: e.target.value })} data-testid="new-source-author" />
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Year (published)</label>
              <input type="number" className="input-rf w-full text-sm" value={newRow.year} onChange={(e) => setNewRow({ ...newRow, year: e.target.value })} data-testid="new-source-year" />
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Country</label>
              <input className="input-rf w-full text-sm" value={newRow.country} onChange={(e) => setNewRow({ ...newRow, country: e.target.value })} data-testid="new-source-country" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Archive link (Internet Archive URL)</label>
              <input className="input-rf w-full text-sm" placeholder="https://archive.org/details/..." value={newRow.archive_url} onChange={(e) => setNewRow({ ...newRow, archive_url: e.target.value })} data-testid="new-source-archive" />
            </div>
          </div>
          <p className="text-xs text-rf-ink-soft italic">
            When you click Save, Nano Banana will draft a period-appropriate cover automatically. You can replace it any time.
          </p>
          <div className="flex justify-end gap-2">
            <button className="btn-outline-ink !py-2 !px-4 text-sm" onClick={() => { setShowAdd(false); setNewRow(EMPTY_FORM); }}>Cancel</button>
            <button className="btn-amber !py-2 !px-4 text-sm inline-flex items-center gap-2 disabled:opacity-60" onClick={createSource} disabled={busy} data-testid="new-source-save">
              <Sparkle size={16} weight="bold" /> Save + draft cover
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-rf-ink-soft">Loading sources…</p>
      ) : (
        <div className="bg-white rounded-2xl border border-rf-ink/10 overflow-hidden">
          <table className="w-full text-sm" data-testid="historical-sources-table">
            <thead className="bg-rf-cream-alt">
              <tr className="text-left">
                <th className="p-3 text-xs font-black uppercase tracking-widest text-rf-ink-soft w-16">Cover</th>
                <th className="p-3 text-xs font-black uppercase tracking-widest text-rf-ink-soft">Book</th>
                <th className="p-3 text-xs font-black uppercase tracking-widest text-rf-ink-soft w-24">Recipes</th>
                <th className="p-3 text-xs font-black uppercase tracking-widest text-rf-ink-soft w-24">Visible</th>
                <th className="p-3 text-xs font-black uppercase tracking-widest text-rf-ink-soft w-56 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.source_id} className="border-t border-rf-ink/5 hover:bg-rf-cream-alt/40" data-testid={`historical-source-row-${r.slug}`}>
                  <td className="p-2">
                    <div className="w-12 aspect-[3/4] rounded overflow-hidden bg-rf-cream-alt flex items-center justify-center">
                      {r.cover_url ? (
                        <img src={r.cover_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <BookOpen size={18} weight="duotone" className="text-rf-ink-soft/60" />
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <p className="font-black text-rf-ink leading-tight">{r.title}</p>
                    <p className="text-xs text-rf-ink-soft">
                      {r.author}{r.year ? ` · ${r.year > 0 ? r.year : Math.abs(r.year) + " BC"}` : ""}{r.country ? ` · ${r.country}` : ""}
                    </p>
                    {r.ai_seeded && <span className="inline-block mt-1 text-[10px] font-black uppercase tracking-widest text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">AI-seeded, needs review</span>}
                    {r.archive_url && (
                      <a href={r.archive_url} target="_blank" rel="noreferrer" className="text-[10px] text-rf-orange font-bold uppercase tracking-widest inline-flex items-center gap-1 mt-1 hover:underline">
                        <ArrowSquareOut size={10} weight="bold" /> Archive
                      </a>
                    )}
                  </td>
                  <td className="p-3 text-rf-ink font-bold">{r.recipe_count || 0}</td>
                  <td className="p-3">
                    <button
                      onClick={() => toggleVisibility(r)}
                      disabled={busy}
                      data-testid={`historical-source-toggle-${r.slug}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black transition-colors ${
                        r.visible ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "bg-rf-ink/10 text-rf-ink-soft hover:bg-rf-ink/20"
                      }`}
                    >
                      {r.visible ? <><Eye size={12} weight="bold" /> Live</> : <><EyeSlash size={12} weight="bold" /> Hidden</>}
                    </button>
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button title="Edit" onClick={() => setEditing(r)} className="p-1.5 rounded hover:bg-rf-ink/10" data-testid={`historical-source-edit-${r.slug}`}>
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      <button title="Regenerate AI cover" onClick={() => regenerateCover(r)} disabled={busy} className="p-1.5 rounded hover:bg-amber-100 text-amber-700 disabled:opacity-40" data-testid={`historical-source-regen-${r.slug}`}>
                        <Sparkle size={14} weight="bold" />
                      </button>
                      <button title="Delete" onClick={() => removeSource(r)} disabled={busy} className="p-1.5 rounded hover:bg-red-100 text-red-600 disabled:opacity-40" data-testid={`historical-source-delete-${r.slug}`}>
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-rf-ink-soft italic">No sources yet — add one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-rf-ink/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="historical-source-edit-modal">
          <div className="bg-white rounded-3xl max-w-2xl w-full my-8 shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-rf-ink/10">
              <div>
                <p className="overline">Edit source</p>
                <h3 className="font-display font-black text-xl text-rf-ink">{editing.title}</h3>
              </div>
              <button onClick={() => setEditing(null)} className="p-2 hover:bg-rf-cream-alt rounded-full">
                <XIcon size={18} weight="bold" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <section>
                <p className="overline mb-2">Cover</p>
                <div className="flex gap-4 items-start">
                  <div className="w-32 aspect-[3/4] rounded-xl overflow-hidden bg-rf-cream-alt flex items-center justify-center flex-shrink-0">
                    {editing.cover_url ? (
                      <img src={editing.cover_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <BookOpen size={32} weight="duotone" className="text-rf-ink-soft/60" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input ref={uploadRef} type="file" accept="image/*" className="hidden" data-testid="edit-source-cover-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCover(editing, f); e.target.value = ""; }} />
                    <button onClick={() => uploadRef.current?.click()} disabled={busy} className="btn-outline-ink w-full inline-flex items-center gap-2 !py-2 !px-3 text-sm disabled:opacity-60" data-testid="edit-source-upload">
                      <UploadSimple size={16} weight="bold" /> Upload true cover
                    </button>
                    <button onClick={() => regenerateCover(editing)} disabled={busy} className="btn-amber w-full inline-flex items-center gap-2 !py-2 !px-3 text-sm disabled:opacity-60" data-testid="edit-source-regen">
                      <Sparkle size={16} weight="bold" /> Regenerate with AI
                    </button>
                    <p className="text-xs text-rf-ink-soft italic">Any file &lt; 10 MB. JPG / PNG / WEBP.</p>
                  </div>
                </div>
              </section>
              <section className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Title *</label>
                  <input className="input-rf w-full text-sm" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="edit-source-title" />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Author</label>
                  <input className="input-rf w-full text-sm" value={editing.author || ""} onChange={(e) => setEditing({ ...editing, author: e.target.value })} data-testid="edit-source-author" />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Year</label>
                  <input type="number" className="input-rf w-full text-sm" value={editing.year ?? ""} onChange={(e) => setEditing({ ...editing, year: e.target.value ? parseInt(e.target.value, 10) : null })} data-testid="edit-source-year" />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Country</label>
                  <input className="input-rf w-full text-sm" value={editing.country || ""} onChange={(e) => setEditing({ ...editing, country: e.target.value })} data-testid="edit-source-country" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Archive link</label>
                  <input className="input-rf w-full text-sm" placeholder="https://archive.org/details/..." value={editing.archive_url || ""} onChange={(e) => setEditing({ ...editing, archive_url: e.target.value })} data-testid="edit-source-archive" />
                </div>
                <div className="md:col-span-2">
                  <label className="inline-flex items-center gap-2 text-sm font-bold text-rf-ink cursor-pointer">
                    <input type="checkbox" checked={!!editing.visible} onChange={(e) => setEditing({ ...editing, visible: e.target.checked })} data-testid="edit-source-visible" />
                    Visible on /historical
                  </label>
                </div>
              </section>
              <div className="flex justify-end gap-2 pt-2 border-t border-rf-ink/10">
                <button className="btn-outline-ink !py-2 !px-4 text-sm" onClick={() => setEditing(null)}>Cancel</button>
                <button className="btn-amber !py-2 !px-4 text-sm inline-flex items-center gap-2 disabled:opacity-60" onClick={saveEdits} disabled={busy} data-testid="edit-source-save">
                  Save changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
